import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  authApiClient,
  bxApiClient,
} from "@shared/api/clients/apiClientFactory";
import { QueryStringParameters } from "@shared/api/url-builder";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

export const getGlCodes = async (
  queryParams?: Record<string, any>,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "gl-codes",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
      if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            qs.push(key, value.toString());
          }
        });
      }
    },
  );

  const response = await bxApiClient.get(url);
  return response.data;
};

export const getGlCodesGrouped = async (
  queryParams?: Record<string, any>,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "gl-codes/grouped",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
      if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            qs.push(key, value.toString());
          }
        });
      }
    },
  );

  const response = await bxApiClient.get(url);

  return response.data;
};

export const getGLCode = async (
  queryParams?: Record<string, any>,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "gl-code-settings",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          qs.push(key, value.toString());
        }
      });
    },
  );

  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const updateGlCode = async (payload: any) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `gl-code-settings`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
    },
  );
  const response = await bxApiClient.put(url, payload);
  return response.data?.result;
};

export const updateGlCodeById = async ({
  id,
  payload,
}: {
  id: string | number;
  payload: any;
}) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `gl-codes/${id}`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
    },
  );
  const response = await bxApiClient.put(url, payload);
  return response.data?.result;
};

export const createGlCodes = async (payload: any) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `gl-codes/multi`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
    },
  );
  const response = await bxApiClient.post(url, payload);
  return response.data?.result;
};

export const updateGlCodesMulti = async (payload: any) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `gl-codes/multi`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
    },
  );
  const response = await bxApiClient.patch(url, payload);
  return response.data?.result;
};

export const updateGlCodeStatus = async ({
  id,
  status,
}: {
  id: string | number;
  status: number;
}) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrl("bx", `gl-codes/${id}/status`);
  const response = await bxApiClient.patch(url, {
    status,
    remote_utility_id: Number(REMOTE_UTILITY_ID)
  });
  return response.data?.result;
};

export const getGlCodeDetails = async (id: string | number): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `gl-codes/${id}/detail`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
    },
  );
  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getUtilityServices = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "auth",
    `utility/services`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );
  const response = await authApiClient.get(url);

  const data = response.data?.result;
  return data.map((item: any) => ({
    id: item.name,
    name: item.name,
  }));
};

export const getSummary = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `gl-codes-settings/summary`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );
  const response = await authApiClient.get(url);

  const data = response.data?.result;
  return data;
};

export const getChoices = async (choiceType: string): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `gl-codes/choices/${choiceType}`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
    },
  );
  const response = await bxApiClient.get(url);

  let data = response.data?.result || response.data;

  // If the response is an object with the choiceType as a key, extract it
  if (
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    data[choiceType]
  ) {
    data = data[choiceType];
  }

  return data || [];
};

export const getGlSources = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `gl-codes/choices/gl-sources`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
    },
  );
  const response = await bxApiClient.get(url);

  return response.data?.result || response.data || [];
};

export const getTransactionCategories = async (
  sources?: string,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `gl-codes/choices/transaction-categories`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
      if (sources) {
        qs.push("sources", sources);
      }
    },
  );
  const response = await bxApiClient.get(url);

  return response.data?.result || response.data || [];
};
export const getTransactionCategoriesMultiple = async (
  sources?: string[],
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `gl-codes/choices/transaction-categories`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
      if (sources) {
        for (const source of sources) {
          qs.push("sources", source);
        }
      }
    },
  );
  const response = await bxApiClient.get(url);

  return response.data?.result || response.data || [];
};

export const getTransactionTypes = async (
  transactionCategory?: string,
): Promise<string[]> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `gl-codes/choices/transaction-types`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
      if (transactionCategory) {
        qs.push("transaction_category", transactionCategory);
      }
    },
  );
  const response = await bxApiClient.get(url);

  const result = response.data?.result;
  // Response shape: { transaction_category, transaction_category_display, transaction_types: string[] }
  if (result?.transactionTypes && Array.isArray(result.transactionTypes)) {
    return result.transactionTypes;
  }
  return result || response.data || [];
};

export const getCsvTemplate = async (params?: {
  source?: string;
  transactionCategory?: string;
}): Promise<Blob> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `gl-codes/csv/template`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
      if (params?.source) {
        qs.push("source", params.source);
      }
      if (params?.transactionCategory) {
        qs.push("transaction_category", params.transactionCategory);
      }
    },
  );
  const response = await bxApiClient.get(url, { responseType: "blob" });
  return response.data;
};

export const importCsvGlCodes = async (
  file: File,
  action: "preview" | "save",
  onProgress?: (percent: number) => void,
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `gl-codes/csv/import`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
    },
  );

  const formData = new FormData();
  formData.append("remote_utility_id", REMOTE_UTILITY_ID.toString());
  formData.append("action", action);
  formData.append("file", file);

  const response = await bxApiClient.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total) {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        onProgress?.(percent);
      }
    },
  });

  return response.data;
};

export const exportGlCodes = async (
  queryParams?: Record<string, any>,
): Promise<Blob> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "gl-codes/export",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
      if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            qs.push(key, value.toString());
          }
        });
      }
    },
  );

  const response = await bxApiClient.get(url, { responseType: "blob" });
  return response.data;
};
